import Isologo from "@/assets/images/logos/isologo.webp";
import { LoaderButton } from "@/components/LoaderButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useAuthActions,
  useAuthError,
  useAuthLoading,
  useAuthUser,
} from "@/context/auth/AuthContext";
import { meUserWithOrg } from "@/context/auth/authUtils";
import {
  assertSessionCurrent,
  saveIdentitySession,
  sessionSnapshot,
} from "@/context/auth/sessionRenewal";
import { IpcRest } from "@/hooks/ipc";
import loadStore from "@/utils/generateStore";
import { Form, FormikProvider, useFormik } from "formik";
import { Eye, EyeOff } from "lucide-react";
import { SetStateAction, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { destinoDespuesDelLogin } from "@/utils/nextDestination";
import * as Yup from "yup";
import Particles from "./Particles";
import SelectBusiness, {
  type Data as SelectBusinessData,
} from "./select-business/SelectBusiness";
import VerificationInput from "./select-business/verification-input/VerificationInput";
import EntrarALaCajaSinSesion from "./caja/EntrarALaCajaSinSesion";

export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const IDENTITY_AUTH_ENABLED =
  import.meta.env.VITE_IDENTITY_AUTH_ENABLED === "true";

// TODO(identity): en prod el org no puede venir de una env var fija — hay que
// resolverlo por subdominio o con un campo "Organización" en el form de login.
const IDENTITY_ORG_SLUG = import.meta.env.VITE_IDENTITY_ORG_SLUG;

type IdentityBusinessType = "employee" | "owner" | "buyer" | "organizer";

// identity manda businesses = {business_id, name, role, scope_level}; el
// SelectBusiness existente espera {id, businessId, businessName, type} → mapeo
// role→type y name→businessName.
const mapIdentityRoleToType = (
  role?: string,
  scopeLevel?: string
): IdentityBusinessType => {
  // Una membresía `org` (organizador de la organización) NO es "propietario" del
  // negocio: desde identity ADR-0016 el picker ya no la trae; si una versión vieja
  // la mandara, se rotula aparte para que nunca más se lea como lo del 2026-09-16
  // (el owner de la org viendo los 11 negocios de sus clientes como suyos).
  if ((scopeLevel ?? "").toLowerCase() === "org") return "organizer";
  const r = (role ?? "").toLowerCase();
  if (r.includes("buyer") || r.includes("client")) return "buyer";
  if (r.includes("employee") || r.includes("staff") || r.includes("member"))
    return "employee";
  return "owner";
};

function LoginOne({ showSelectBusinessProp = false, businessDataProp = null }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // A dónde volver al entrar. Lo pone quien nos mandó acá (ClientLayout o el
  // guard de Ventas B2B) cuando la persona venía a una pantalla concreta —
  // típicamente un link de un mail. `destinoDespuesDelLogin` descarta cualquier
  // cosa que no sea una ruta interna, así que un `next` a otro host no lleva a
  // nadie afuera después de poner la contraseña.
  const destino = destinoDespuesDelLogin(params.get("next"));

  const { startLogin, finishLogin, loginSuccess, loginError, logout } =
    useAuthActions();
  const loading = useAuthLoading();
  const error = useAuthError();
  const user = useAuthUser();

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getTokenAndGoToHome = async () => {
    startLogin();

    try {
      const store = await loadStore();
      const token = await store.get("token");

      if (!token) {
        throw new Error("No token found in local storage");
      }

      if (!user) {
        throw new Error("No user found in context");
      }

      document.cookie = `token=${token}; path=/`;

      navigate("/dashboard");
    } catch {
      logout();
    }
  };

  useEffect(() => {
    getTokenAndGoToHome();
  }, []);

  const [showSelectBusiness, setShowSelectBusiness] = useState(
    showSelectBusinessProp
  );
  const [businessesData, setBusinessesData] =
    useState<SelectBusinessData | null>(businessDataProp);
  const [show2fa, setShow2fa] = useState(false);
  const [codeState, setCodeState] = useState("");
  // La caja sin sesión: el cajero entra con su PIN (trd-client#346).
  const [conPin, setConPin] = useState(false);

  const [userId, setUserId] = useState<string | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);

  const onValidationInputsChange = (value: SetStateAction<string>) => {
    setCodeState(value);
  };

  const onSubmitCode = async () => {
    const store = await loadStore();
    startLogin();

    try {
      const res = await IpcRest.post<any>({
        url: "/api/v1/auth/verifycode",
        body: { code: +codeState, businessId, userId },
      });

      await store.set("token", res.accessToken);
      await store.set("refreshToken", res.refreshToken);
      document.cookie = `token=${res.accessToken}; path=/`;

      const me = await IpcRest.get<any>({ url: "/api/v1/auth/me" });

      // El `/me` flag-ON devuelve el negocio en `business.id`, no en
      // `user.businessId`: leerlo crudo guardaba el STRING "undefined", que es
      // truthy, así que el interceptor lo mandaba como header `businessid` y el
      // BFF respondía 500 (p. ej. al crear un plan de precios). `meUserWithOrg`
      // ya tolera las dos formas del envelope.
      await store.set("businessId", meUserWithOrg(me)?.businessId);
      await store.set("user", me?.user);
      await loginSuccess(me?.user);

      navigate(destino);
    } catch (error) {
      if (error && (error as any).response) {
        loginError((error as any).response.data.statusCode);
      } else {
        loginError("Unknown error");
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },

    validationSchema: Yup.object({
      email: Yup.string()
        .matches(emailRegex, "El email no es válido")
        .required("El email es requerido"),
      password: Yup.string()
        .min(5, "Tiene que ser mayor a 5 caracteres")
        .required("La contraseña es requerida"),
    }),

    onSubmit: async function (values) {
      const store = await loadStore();
      startLogin();

      // ── IDENTITY (org-scoped), detrás del flag. El legacy queda intacto abajo.
      if (IDENTITY_AUTH_ENABLED) {
        const before = await sessionSnapshot(store);
        if (!IDENTITY_ORG_SLUG) {
          finishLogin();
          loginError({
            userMessage:
              // `trud-dev`, NO `trud-dev-org` (que no existe). El slug malo
              // cuesta caro: identity es fail-closed y una org inexistente
              // devuelve el MISMO 401 que una password equivocada, así que el
              // cartel mandaba a debuggear las credenciales en vez del slug.
              "Falta configurar VITE_IDENTITY_ORG_SLUG (dev: trud-dev).",
          });
          return;
        }

        try {
          const res = await IpcRest.post<any>({
            url: "/api/v1/auth/local/signin",
            body: {
              organization_slug: IDENTITY_ORG_SLUG,
              email: values.email.trim(),
              password: values.password.trim(),
            },
          });
          // El BFF/identity envuelven la respuesta en { data, statusCode }.
          const auth = res?.data ?? res;
          await assertSessionCurrent(store, before);

          // 2FA: identity usa POST /api/v1/auth/2fa/verify (distinto del
          // /verifycode legacy) → queda como follow-up.
          if (auth.two_factor_required) {
            finishLogin();
            loginError({
              userMessage:
                "Tu cuenta requiere 2FA; el flujo 2FA de identity está pendiente (follow-up).",
            });
            return;
          }

          const businesses: Array<{
            business_id: string;
            name?: string;
            role?: string;
            scope_level?: string;
          }> = auth.businesses ?? [];

          // Más de un business → reusar SelectBusiness con la data normalizada
          // (picker_token→tempToken, businesses→assignedBusiness).
          if (businesses.length > 1) {
            setBusinessesData({
              tempToken: auth.picker_token,
              refreshToken: auth.refresh_token,
              sessionSnapshot: before,
              assignedBusiness: businesses.map((b) => ({
                id: b.business_id,
                businessId: b.business_id,
                // El picker muestra el NOMBRE del negocio, no su id. Fallback al
                // id sólo si identity no mandara `name` (no debería).
                businessName: b.name ?? b.business_id,
                type: mapIdentityRoleToType(b.role, b.scope_level),
              })),
            });
            setShowSelectBusiness(true);
            finishLogin();
            return;
          }

          const only = businesses[0];
          if (!only) {
            throw new Error("El usuario no tiene negocios asignados.");
          }

          // Un solo business → select-business directo (token SCOPED en `token`).
          const scoped = await IpcRest.post<any>({
            url: "/api/v1/auth/business",
            body: { business_id: only.business_id },
            headers: { Authorization: `Bearer ${auth.picker_token}` },
          });
          const scopedAuth = scoped?.data ?? scoped;

          const me = await IpcRest.get<any>({
            url: "/api/v1/auth/me",
            headers: { Authorization: `Bearer ${scopedAuth.token}` },
          });
          const meUser = meUserWithOrg(me);
          await saveIdentitySession(
            store,
            scopedAuth.token,
            auth.refresh_token,
            meUser,
            before
          );

          navigate(destino);
        } catch (error) {
          // This path has not installed credentials on failure. A later login
          // or logout owns the UI now, so don't clear or overwrite its state.
          try {
            await assertSessionCurrent(store, before);
          } catch {
            return;
          }
          loginError(error);
        }
        return;
      }

      try {
        const res = await IpcRest.post<any>({
          url: "/api/v1/auth/local/signin",
          body: {
            email: values.email.trim(),
            password: values.password.trim(),
          },
        });

        // Si tiene más de un business
        if (res.tempToken && res.assignedBusiness.length > 1) {
          setShowSelectBusiness(true);
          setBusinessesData(res);
          finishLogin();
          return;
        }

        // Si tiene un solo business, y FA true
        if (res.has2fa) {
          setShow2fa(true);
          setUserId(res.userId);
          setBusinessId(res.businessId);
          finishLogin();
          return;
        }

        await store.set("token", res.accessToken);
        await store.set("refreshToken", res.refreshToken);
        document.cookie = `token=${res.accessToken}; path=/`;

        const me = await IpcRest.get<any>({ url: "/api/v1/auth/me" });

        // El `/me` flag-ON devuelve el negocio en `business.id`, no en
        // `user.businessId`: leerlo crudo guardaba el STRING "undefined", que es
        // truthy, así que el interceptor lo mandaba como header `businessid` y el
        // BFF respondía 500 (p. ej. al crear un plan de precios). `meUserWithOrg`
        // ya tolera las dos formas del envelope.
        await store.set("businessId", meUserWithOrg(me)?.businessId);
        await store.set("user", me?.user);

        await loginSuccess(me?.user);

        navigate(destino);
      } catch (error) {
        await store.delete("token");

        if (error && (error as any).response) {
          loginError(error);
        } else {
          loginError("Unknown error");
        }
      }
    },
  });

  return (
    <div className="bg-slate-900 w-full min-h-screen text-text-primary flex flex-col justify-center items-center md:flex-row relative p-5 overflow-hidden">
      <Particles className="absolute inset-0" quantity={250} />

      <div className="grid md:grid-cols-2 gap-10 items-center p-6 max-w-5xl mx-auto grid-cols-1">
        {/* First column */}
        <div className="relative">
          {/* Blurred shape */}
          <div
            className="absolute -top-1/2 left-0 -mt-24 -ml-16 pointer-events-none blur-2xl opacity-70"
            aria-hidden="true"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="434" height="427">
              <defs>
                <linearGradient
                  id="bs4-a"
                  x1="19.609%"
                  x2="50%"
                  y1="14.544%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#ff4241" />
                  <stop offset="100%" stopColor="#ff0086" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                fill="url(#bs4-a)"
                fillRule="evenodd"
                d="m0 0 461 369-284 58z"
                transform="matrix(1 0 0 -1 0 427)"
              />
            </svg>
          </div>

          <div className="">
            {/* Section header */}
            <div>
              <h1 className="text-4xl md:text-5xl text-center md:text-left lg:text-6xl pb-2 lg:pb-4 font-bold text-transparent bg-clip-text bg-gradient-to-r from-slate-200/60 via-slate-200 to-slate-200/60">
                ¡Hola de nuevo!
              </h1>
              <div className="text-xl text-slate-300 text-center md:text-left">
                Te damos la bienvenida a
                <img
                  src={Isologo}
                  alt="logo"
                  className="inline w-18 mx-2 mb-1"
                />
                Por favor, inicia sesión para continuar.
              </div>
            </div>
          </div>
        </div>

        {/* Second column */}
        <div className="relative">
          {/* Blurred shape */}
          <div
            className="z-10 absolute bottom-3/4 left-0 translate-y-1/2 opacity-50 pointer-events-none blur-2xl"
            aria-hidden="true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="868"
              height="854"
              viewBox="0 0 434 427"
            >
              <defs>
                <linearGradient
                  id="bs5-a"
                  x1="19.609%"
                  x2="50%"
                  y1="14.544%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#ff4142" />
                  <stop offset="100%" stopColor="#ff0086" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                fill="url(#bs5-a)"
                fillRule="evenodd"
                d="m0 0 461 369-284 58z"
                transform="matrix(1 0 0 -1 0 427)"
              />
            </svg>
          </div>

          <div className="relative flex flex-col items-center justify-center z-20 bg-slate-900 rounded-xl p-8 min-h-96">
            {!showSelectBusiness && !show2fa && !conPin && (
              <FormikProvider value={formik}>
                <Form
                  className="w-full flex flex-col items-center justify-center gap-6"
                  onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit(e);
                  }}
                >
                  <div className="grid w-full items-center gap-2">
                    <Label
                      htmlFor="email"
                      className="text-slate-300 font-normal"
                    >
                      Email
                    </Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Introduzca su email..."
                      className="border border-slate-700 text-slate-200 focus-visible:outline-none focus-visible:ring focus-visible:ring-slate-200 focus-visible:ring-offset-1"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.email}
                    />

                    {formik.errors.email && formik.touched.email && (
                      <p className="text-red-500 text-sm">
                        {formik.errors.email}
                      </p>
                    )}

                    {error.email && (
                      <p className="text-red-500 text-sm">{error.email}</p>
                    )}
                  </div>

                  <div className="grid w-full items-center gap-2">
                    <Label
                      htmlFor="password"
                      className="text-slate-300 font-normal"
                    >
                      Contraseña
                    </Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        placeholder="Introduzca su contraseña..."
                        className="border border-slate-700 text-slate-200 focus-visible:outline-none focus-visible:ring focus-visible:ring-slate-200 focus-visible:ring-offset-1"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.password}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={togglePasswordVisibility}
                        aria-label={
                          showPassword
                            ? "Ocultar contraseña"
                            : "Mostrar contraseña"
                        }
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>

                    {formik.errors.password && formik.touched.password && (
                      <p className="text-red-500 text-sm">
                        {formik.errors.password}
                      </p>
                    )}

                    {error.password && (
                      <p className="text-red-500 text-sm">{error.password}</p>
                    )}

                    {error.generic && (
                      <p className="text-red-500 text-sm">{error.generic}</p>
                    )}
                  </div>

                  <LoaderButton
                    type="submit"
                    isLoading={loading}
                    className="self-start"
                  >
                    {loading ? "Iniciando sesión..." : "Iniciar sesión"}
                  </LoaderButton>
                </Form>
              </FormikProvider>
            )}

            {!showSelectBusiness && !show2fa && (
              <EntrarALaCajaSinSesion
                abierto={conPin}
                onAbrir={() => setConPin(true)}
                onCerrar={() => setConPin(false)}
              />
            )}

            {showSelectBusiness && !show2fa && (
              <SelectBusiness
                data={businessesData}
                setShowSelectBusiness={setShowSelectBusiness}
                setBusinessesData={setBusinessesData}
                userId={userId}
                setUserId={setUserId}
                businessId={businessId}
                setBusinessId={setBusinessId}
                show2fa={show2fa}
                setShow2fa={setShow2fa}
              />
            )}

            {show2fa && (
              <div className="w-4/5 flex flex-col gap-4">
                <div className="flex flex-col">
                  <p className="text-slate-200 text-xl">Introduce tu código</p>
                  <p className="text-slate-400">
                    Introduce el código que te hemos enviado a tu correo
                    electrónico.
                  </p>
                </div>

                <VerificationInput
                  onChange={onValidationInputsChange}
                  length={5}
                  loading={loading}
                  defaultValue={codeState}
                />

                <Button onClick={onSubmitCode}>Enviar</Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 bg-slate-900 w-full flex items-center justify-center py-2">
        <div className="text-xs text-text-secondary">
          {`${new Date().getFullYear()}`}{" "}
          <span className="font-semibold">Trud</span>®{" "}
          {`🧡 Todos los derechos reservados.`}
        </div>
      </div>
    </div>
  );
}

export default LoginOne;
